import React, { useState, useEffect, useRef, useCallback } from "react";
import { Camera, Upload, RefreshCw, Bell, LogOut, FileText, Loader2, Check, AlarmClock, FolderOpen, Lock, Trash2 } from "lucide-react";
import { scanDocs, listDocuments, getReminders, toggleReminder, getLanguages, deleteDocument, localizeDocument } from "../lib/api";
import { useAudio } from "./AudioProvider";
import { SpeakerButton } from "./SpeakerButton";
import CameraCapture from "./CameraCapture";
import DocumentModal from "./DocumentModal";
import { makeT } from "../lib/i18n";
import { toast } from "sonner";

function DocCard({ doc, onOpen, onDelete }) {
  const important = doc.verdict === "important";
  return (
    <div
      data-testid={`document-card-${doc.id}`}
      className={`relative text-left yb-tactile p-4 w-full ${important ? "bg-[#DCFCE7]" : "bg-[#FEE2E2]"}`}
      style={{ boxShadow: `0 4px 0 0 ${important ? "#15803D" : "#DC2626"}`, borderColor: important ? "#15803D" : "#DC2626" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(doc); }}
        data-testid={`document-delete-${doc.id}`}
        className="absolute top-2 right-2 z-10 w-9 h-9 rounded-xl bg-white border-2 border-[#1A1C19] flex items-center justify-center hover:bg-[#FEE2E2] transition-colors"
        aria-label="Delete document"
      >
        <Trash2 className="w-4 h-4 text-[#DC2626]" />
      </button>
      <button onClick={() => onOpen(doc)} className="text-left w-full" data-testid={`document-open-${doc.id}`}>
        <div className="flex items-start gap-3 pr-8">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center border-2 shrink-0 ${important ? "bg-[#15803D] text-white border-[#15803D]" : "bg-[#DC2626] text-white border-[#DC2626]"}`}>
            <FileText className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h4 className="yb-heading font-extrabold text-lg leading-tight truncate">{doc.title}</h4>
            <p className="text-sm text-[#50564F] line-clamp-2">{doc.summary}</p>
            {doc.has_deadline && (
              <span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-[#1A1C19]">
                ⏰ {doc.deadline_label}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

export default function Dashboard({ session, onLogout }) {
  const { speak } = useAudio();
  const uiLang = ["en", "ar", "gu"].includes(session.ui_lang) ? session.ui_lang : "gu";
  const t = makeT(uiLang);
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(session.language || (uiLang === "en" ? "en" : "gu"));
  const [docs, setDocs] = useState([]);
  const [displayDocs, setDisplayDocs] = useState([]);
  const [localizing, setLocalizing] = useState(false);
  const localizeCache = useRef({});
  const [reminders, setReminders] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [active, setActive] = useState(null);
  const uploadRef = useRef(null);
  const filesRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([listDocuments(session.user_id), getReminders(session.user_id)]);
      setDocs(d);
      setReminders(r);
    } catch (e) { /* ignore */ }
  }, [session.user_id]);

  useEffect(() => {
    getLanguages().then(setLanguages).catch(() => {});
    (async () => {
      let r = [];
      try {
        const [d, rem] = await Promise.all([listDocuments(session.user_id), getReminders(session.user_id)]);
        setDocs(d);
        setReminders(rem);
        r = rem;
      } catch (e) { /* ignore */ }
      let msg = session.welcome_text;
      const urgent = r.filter((x) => x.urgency === "high");
      if (urgent.length > 0) {
        msg += " " + t("remSpeak", urgent.length, urgent.map((x) => x.deadline_label).filter(Boolean).join(". "));
      }
      setTimeout(() => speak(msg, 1.0, "welcome"), 600);
    })();
  }, []); // eslint-disable-line

  const runScan = async (files) => {
    if (!files || files.length === 0) return;
    setScanning(true);
    speak(t("scanningVoice"), 1.0, "scanning");
    try {
      const doc = await scanDocs(session.user_id, language, Array.from(files));
      await refresh();
      setActive(doc);
      speak(doc.spoken_text, 1.0, "read");
    } catch (e) {
      toast.error(t("scanErrToast"));
      speak(t("scanErrVoice"), 1.0, "err");
    } finally {
      setScanning(false);
    }
  };

  const markDone = async (id) => {
    await toggleReminder(id);
    refresh();
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(t("deleteConfirm"))) return;
    setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    setDisplayDocs((prev) => prev.filter((d) => d.id !== doc.id));
    try {
      await deleteDocument(doc.id);
      toast.success(t("deletedToast"));
    } catch (e) {
      toast.error(t("scanErrToast"));
      refresh();
    }
  };

  // Show existing documents in the currently selected language automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (docs.length === 0) { setDisplayDocs([]); return; }
      const needsWork = docs.some((d) => d.language !== language && !localizeCache.current[`${d.id}:${language}`]);
      if (needsWork) setLocalizing(true);
      const out = await Promise.all(docs.map(async (d) => {
        if (d.language === language) return d;
        const key = `${d.id}:${language}`;
        if (localizeCache.current[key]) return { ...d, ...localizeCache.current[key] };
        try {
          const loc = await localizeDocument(d.id, language);
          localizeCache.current[key] = loc;
          return { ...d, ...loc };
        } catch (e) { return d; }
      }));
      if (!cancelled) { setDisplayDocs(out); setLocalizing(false); }
    })();
    return () => { cancelled = true; };
  }, [docs, language]);

  const hasHigh = reminders.some((r) => r.urgency === "high");
  const grouped = displayDocs.reduce((acc, d) => {
    const s = d.sender || t("others");
    (acc[s] = acc[s] || []).push(d);
    return acc;
  }, {});

  const helpText = t("dashHelp");

  return (
    <div className="min-h-screen pb-24" dir={uiLang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <header className="bg-[#FFD600] border-b-3 border-[#1A1C19] px-4 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 data-testid="dashboard-greeting" className="yb-heading text-2xl sm:text-3xl font-extrabold truncate">
              {t("greeting", session.name)}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SpeakerButton text={helpText} tag="dash-help" testId="dashboard-help-button" />
            <button onClick={onLogout} data-testid="logout-button" className="yb-tactile bg-white p-3" aria-label="બહાર નીકળો">
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {session.role !== "master" && (
          <div data-testid="limited-access-banner" className="rounded-2xl border-3 border-[#1A1C19] bg-[#1A1C19] text-white p-4 flex items-center gap-3" style={{ boxShadow: "0 4px 0 0 #FFD600" }}>
            <div className="w-10 h-10 rounded-xl bg-[#FFD600] text-[#1A1C19] flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-extrabold">{t("limitedTitle")}</p>
              <p className="text-sm text-white/70">{t("limitedBody")}</p>
            </div>
          </div>
        )}

        {/* Reminder banner */}
        {reminders.length > 0 && (
          <div
            data-testid="reminder-banner"
            className={`rounded-2xl border-3 p-4 ${hasHigh ? "bg-[#FEE2E2] border-[#DC2626] yb-glow-red" : "bg-[#FEF3C7] border-[#D97706]"}`}
            style={{ boxShadow: `0 4px 0 0 ${hasHigh ? "#DC2626" : "#D97706"}` }}
          >
            <div className="flex items-center gap-3">
              <Bell className={`w-8 h-8 ${hasHigh ? "text-[#DC2626]" : "text-[#D97706]"}`} />
              <div className="flex-1">
                <h3 className="yb-heading font-extrabold text-lg">{t("remRemember", reminders.length)}</h3>
              </div>
              <SpeakerButton
                text={t("remSpeak", reminders.length, reminders.map((r) => r.deadline_label).filter(Boolean).join(". "))}
                tag="rem-help"
                testId="reminder-speak-button"
              />
            </div>
            <div className="mt-3 space-y-2">
              {reminders.map((r) => (
                <div key={r.id} className="bg-white rounded-xl border-2 border-[#1A1C19] p-3 flex items-center gap-3">
                  <AlarmClock className={`w-6 h-6 shrink-0 ${r.urgency === "high" ? "text-[#DC2626]" : "text-[#D97706]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{r.title}</p>
                    <p className="text-sm text-[#50564F]">{r.deadline_label}</p>
                  </div>
                  <button onClick={() => markDone(r.id)} data-testid={`reminder-done-${r.id}`} className="yb-tactile bg-[#15803D] text-white p-2" aria-label="થઈ ગયું">
                    <Check className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Language selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-bold text-[#50564F]">{t("langLabel")}</span>
          <select
            data-testid="language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="yb-tactile bg-white px-4 py-3 text-lg font-bold outline-none"
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code}>{l.name} ({l.english})</option>
            ))}
          </select>
        </div>

        {/* Scan buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            data-testid="scanner-camera-button"
            onClick={() => setShowCamera(true)}
            disabled={scanning}
            className="yb-tactile bg-[#FFD600] py-10 px-6 text-2xl font-black flex flex-col items-center gap-3 hover:bg-[#E6C200] disabled:opacity-60"
            style={{ boxShadow: "0 8px 0 0 #1A1C19" }}
          >
            {scanning ? <Loader2 className="w-14 h-14 animate-spin" /> : <Camera className="w-14 h-14" />}
            {t("camera")}
          </button>
          <button
            data-testid="scanner-upload-button"
            onClick={() => uploadRef.current?.click()}
            disabled={scanning}
            className="yb-tactile bg-white py-10 px-6 text-2xl font-black flex flex-col items-center gap-3 hover:bg-[#FEFCE8] disabled:opacity-60"
            style={{ boxShadow: "0 8px 0 0 #1A1C19" }}
          >
            <Upload className="w-14 h-14" />
            {t("upload")}
          </button>
          <button
            data-testid="scanner-files-button"
            onClick={() => filesRef.current?.click()}
            disabled={scanning}
            className="yb-tactile bg-white py-10 px-6 text-2xl font-black flex flex-col items-center gap-3 hover:bg-[#FEFCE8] disabled:opacity-60"
            style={{ boxShadow: "0 8px 0 0 #1A1C19" }}
          >
            <FolderOpen className="w-14 h-14" />
            {t("files")}
          </button>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="scanner-file-input"
            onChange={(e) => { runScan(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={filesRef}
            type="file"
            accept="application/pdf,image/*"
            multiple
            className="hidden"
            data-testid="scanner-files-input"
            onChange={(e) => { runScan(e.target.files); e.target.value = ""; }}
          />
        </div>

        {/* Gallery */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="yb-heading text-2xl font-extrabold">{t("myDocs")}</h2>
            <div className="flex items-center gap-2">
              {localizing && <Loader2 className="w-5 h-5 animate-spin text-[#50564F]" data-testid="localizing-spinner" />}
              <button onClick={refresh} data-testid="gallery-refresh" className="yb-tactile bg-white p-3" aria-label="તાજું કરો">
                <RefreshCw className="w-6 h-6" />
              </button>
            </div>
          </div>
          {displayDocs.length === 0 ? (
            <div className="bg-white rounded-2xl border-3 border-dashed border-[#1A1C19] p-8 text-center text-[#50564F] text-lg">
              {t("emptyDocs")}
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(grouped).map(([sender, items]) => (
                <div key={sender}>
                  <h3 className="font-bold text-[#50564F] mb-2">{sender}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map((d) => <DocCard key={d.id} doc={d} onOpen={setActive} onDelete={removeDoc} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {showCamera && (
        <CameraCapture
          uiLang={uiLang}
          onClose={() => setShowCamera(false)}
          onDone={(pages) => { setShowCamera(false); runScan(pages); }}
        />
      )}
      {active && (
        <DocumentModal
          doc={active}
          language={language}
          uiLang={uiLang}
          onClose={() => setActive(null)}
          onSaved={refresh}
          onDeleted={() => { setActive(null); refresh(); }}
        />
      )}
    </div>
  );
}
